import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './MonitorDepartment.css';
import collegeConfig from '../../config/collegeConfig';
import { generateMonitorReport } from '../../utils/monitorReportGenerator';
import StudentDocumentsModal from '../shared/StudentDocumentsModal';
import EligibilityCards from '../shared/EligibilityCards';
import {
  FaBuilding,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaBookMedical,
  FaCheckCircle,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaExclamationTriangle,
  FaLayerGroup,
  FaClipboardList,
  FaSearch,
  FaTimes,
  FaBell,
  FaFilePdf,
  FaDownload,
  FaClipboardCheck,
  FaSyncAlt,
  FaFilter
} from 'react-icons/fa';

const getMonthName = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const MonitorDepartment = ({ user, assignedCourse }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [allEntries, setAllEntries] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [courseTeachers, setCourseTeachers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    teachers: true,
    compliance: true,
    activities: true,
    students: true,
    eligibility: true
  });
  const [expandedTeachers, setExpandedTeachers] = useState({});
  const [studentSearch, setStudentSearch] = useState('');
  const [studentSort, setStudentSort] = useState({ field: 'name', direction: 'asc' });
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const monthPickerRef = React.useRef(null);
  const [sendingGuideNotif, setSendingGuideNotif] = useState(false);
  const [guideNotifResult, setGuideNotifResult] = useState(null);
  const [notifyingGuide, setNotifyingGuide] = useState(null);
  const [guideNotifResults, setGuideNotifResults] = useState({});
  const [generatingReport, setGeneratingReport] = useState(null); // 'daily'|'weekly'|'monthly'|null

  // ── Exam Eligibility state ─────────────────────────────────────────────────
  const [eligibilityBatch, setEligibilityBatch] = useState('');
  const [eligibilityList, setEligibilityList] = useState([]);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState('');
  const [eligibilityFetched, setEligibilityFetched] = useState(false);
  const [docsModalStudent, setDocsModalStudent] = useState(null);
  const [batchLabels, setBatchLabels] = useState({});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    fetch(`${collegeConfig.api.baseURL}/system-settings`)
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.batchLabels)) {
          setBatchLabels(Object.fromEntries(d.batchLabels.map(b => [b.batch, b.label])));
        }
      })
      .catch(() => {});
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!assignedCourse) return;

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const [entriesRes, studentsRes, courseRes] = await Promise.all([
        fetch(
          `${collegeConfig.api.baseURL}/logbook/entries/course/${assignedCourse._id}`,
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
        ),
        fetch(
          `${collegeConfig.api.baseURL}/students/by-course/${assignedCourse._id}`,
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
        ),
        fetch(
          `${collegeConfig.api.baseURL}/courses/${assignedCourse._id}`,
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
        )
      ]);

      if (entriesRes.ok) {
        const entriesData = await entriesRes.json();
        setAllEntries(entriesData.entries || []);
      }

      if (studentsRes.ok) {
        const studentsData = await studentsRes.json();
        setAllStudents(studentsData.students || []);
      }

      if (courseRes.ok) {
        const courseData = await courseRes.json();
        setCourseTeachers(courseData.course?.teachers || []);
        setModules(courseData.course?.modules || []);
      }
    } catch (err) {
      console.error('Error fetching department data:', err);
      setError('Failed to load department data');
    } finally {
      setLoading(false);
    }
  }, [assignedCourse]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Distinct batches from allStudents
  const availableBatches = useMemo(() => {
    const batchSet = new Set();
    allStudents.forEach(s => {
      const b = s.assignedCourses?.find(c => c.courseId?.toString() === assignedCourse?._id?.toString())?.batch;
      if (b) batchSet.add(b);
    });
    return Array.from(batchSet).sort();
  }, [allStudents, assignedCourse]);

  const fetchEligibilityBatch = useCallback(async () => {
    if (!assignedCourse) return;
    setEligibilityLoading(true);
    setEligibilityError('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ courseId: assignedCourse._id });
      if (eligibilityBatch) params.set('batch', eligibilityBatch);
      const res = await fetch(
        `${collegeConfig.api.baseURL}/exam-eligibility/batch?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        setEligibilityList(data.eligibilityList || []);
        setEligibilityFetched(true);
      } else {
        setEligibilityError(data.message || 'Failed to fetch eligibility data');
      }
    } catch (e) {
      setEligibilityError('Network error. Please try again.');
    } finally {
      setEligibilityLoading(false);
    }
  }, [assignedCourse, eligibilityBatch]);

  // Filter entries by selected month (excluding drafts)
  const monthEntries = useMemo(() => {
    return allEntries.filter(entry => {
      if (entry.status === 'draft') return false;
      const entryDate = new Date(entry.metadata?.submittedAt || entry.createdAt);
      const entryMonth = entryDate.toISOString().slice(0, 7);
      return entryMonth === selectedMonth;
    });
  }, [allEntries, selectedMonth]);

  // Overview stats
  const overviewStats = useMemo(() => {
    const totalStudents = allStudents.length;
    const totalTeachers = courseTeachers.length;
    const totalEntries = monthEntries.length;
    const approvedEntries = monthEntries.filter(e => e.status === 'approved').length;
    const verificationRate = totalEntries > 0 ? Math.round((approvedEntries / totalEntries) * 100) : 0;

    return { totalStudents, totalTeachers, totalEntries, verificationRate };
  }, [allStudents, courseTeachers, monthEntries]);

  // Teacher verification data
  const teacherData = useMemo(() => {
    const teacherMap = {};

    // Initialize from course teachers
    courseTeachers.forEach(t => {
      const id = (t.teacherId?._id || t.teacherId)?.toString();
      if (id) {
        teacherMap[id] = {
          id,
          name: t.name || 'Unknown Teacher',
          monthVerifications: 0,
          totalVerifications: 0,
          lastVerificationDate: null
        };
      }
    });

    // Process all entries for verifications
    allEntries.forEach(entry => {
      if (!entry.review || !entry.review.reviewedAt) return;
      const reviewerId = (entry.review.reviewerId?._id || entry.review.reviewerId)?.toString();
      if (!reviewerId) return;

      if (!teacherMap[reviewerId]) {
        teacherMap[reviewerId] = {
          id: reviewerId,
          name: entry.review.reviewerName || 'Unknown Teacher',
          monthVerifications: 0,
          totalVerifications: 0,
          lastVerificationDate: null
        };
      }

      const teacher = teacherMap[reviewerId];
      teacher.totalVerifications++;

      const reviewDate = new Date(entry.review.reviewedAt);
      if (!teacher.lastVerificationDate || reviewDate > teacher.lastVerificationDate) {
        teacher.lastVerificationDate = reviewDate;
      }

      const reviewMonth = reviewDate.toISOString().slice(0, 7);
      if (reviewMonth === selectedMonth) {
        teacher.monthVerifications++;
      }
    });

    const teachers = Object.values(teacherMap);
    teachers.sort((a, b) => b.monthVerifications - a.monthVerifications);
    return teachers;
  }, [allEntries, courseTeachers, selectedMonth]);

  const maxTeacherVerifications = useMemo(() => {
    return Math.max(1, ...teacherData.map(t => t.monthVerifications));
  }, [teacherData]);

  // Per-teacher verification details for the selected month
  const teacherVerificationDetails = useMemo(() => {
    const detailsMap = {};

    allEntries.forEach(entry => {
      if (!entry.review || !entry.review.reviewedAt) return;
      const reviewerId = (entry.review.reviewerId?._id || entry.review.reviewerId)?.toString();
      if (!reviewerId) return;

      const reviewDate = new Date(entry.review.reviewedAt);
      const reviewMonth = reviewDate.toISOString().slice(0, 7);
      if (reviewMonth !== selectedMonth) return;

      if (!detailsMap[reviewerId]) {
        detailsMap[reviewerId] = [];
      }

      const studentName = `${entry.studentId?.profile?.firstName || ''} ${entry.studentId?.profile?.lastName || ''}`.trim() || 'Unknown Student';
      const moduleId = entry.moduleId?.toString();
      const moduleInfo = modules.find(m => (m.moduleId || m._id)?.toString() === moduleId);
      const moduleName = moduleInfo?.name || 'Unknown Module';

      detailsMap[reviewerId].push({
        studentName,
        moduleName,
        date: reviewDate,
        status: entry.review.status || entry.status,
        entryId: entry._id
      });
    });

    // Sort each teacher's details by date descending
    Object.keys(detailsMap).forEach(id => {
      detailsMap[id].sort((a, b) => b.date - a.date);
    });

    return detailsMap;
  }, [allEntries, modules, selectedMonth]);

  const toggleTeacherExpand = (teacherId) => {
    setExpandedTeachers(prev => ({ ...prev, [teacherId]: !prev[teacherId] }));
  };

  // Weekly compliance data
  const weeklyCompliance = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);

    const weeks = [];
    let weekStart = new Date(firstDay);
    // Adjust to Monday
    const dayOfWeek = weekStart.getDay();
    if (dayOfWeek !== 1) {
      weekStart.setDate(weekStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    }

    while (weekStart <= lastDay) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      const effectiveStart = new Date(Math.max(weekStart.getTime(), firstDay.getTime()));
      const effectiveEnd = new Date(Math.min(weekEnd.getTime(), lastDay.getTime()));
      effectiveEnd.setHours(23, 59, 59, 999);

      const submittedStudentIds = new Set();
      allEntries.forEach(entry => {
        if (entry.status === 'draft') return;
        const submitDate = new Date(entry.metadata?.submittedAt || entry.createdAt);
        if (submitDate >= effectiveStart && submitDate <= effectiveEnd) {
          const studentId = (entry.studentId?._id || entry.studentId)?.toString();
          if (studentId) submittedStudentIds.add(studentId);
        }
      });

      const nonCompliant = allStudents.filter(s => {
        const sid = (s._id || s.id)?.toString();
        return !submittedStudentIds.has(sid);
      });

      weeks.push({
        weekStart: new Date(effectiveStart),
        weekEnd: new Date(effectiveEnd),
        label: `${formatDate(effectiveStart)} - ${formatDate(effectiveEnd)}`,
        submitted: submittedStudentIds.size,
        total: allStudents.length,
        compliance: allStudents.length > 0
          ? Math.round((submittedStudentIds.size / allStudents.length) * 100)
          : 0,
        nonCompliant
      });

      weekStart = new Date(weekEnd);
      weekStart.setDate(weekStart.getDate() + 1);
    }

    return weeks;
  }, [selectedMonth, allEntries, allStudents]);

  const overallCompliance = useMemo(() => {
    if (weeklyCompliance.length === 0) return 0;
    const avg = weeklyCompliance.reduce((sum, w) => sum + w.compliance, 0) / weeklyCompliance.length;
    return Math.round(avg);
  }, [weeklyCompliance]);

  // Activity distribution (by module)
  const activityDistribution = useMemo(() => {
    const moduleMap = {};

    monthEntries.forEach(entry => {
      const moduleId = entry.moduleId?.toString();
      const moduleInfo = modules.find(m => (m.moduleId || m._id)?.toString() === moduleId);
      const moduleName = moduleInfo?.name || 'Unknown Module';

      if (!moduleMap[moduleName]) {
        moduleMap[moduleName] = { name: moduleName, count: 0, students: new Set() };
      }
      moduleMap[moduleName].count++;
      const studentId = (entry.studentId?._id || entry.studentId)?.toString();
      if (studentId) moduleMap[moduleName].students.add(studentId);
    });

    const activities = Object.values(moduleMap).map(a => ({
      ...a,
      studentCount: a.students.size
    }));
    activities.sort((a, b) => b.count - a.count);
    return activities;
  }, [monthEntries, modules]);

  const maxActivityCount = useMemo(() => {
    return Math.max(1, ...activityDistribution.map(a => a.count));
  }, [activityDistribution]);

  // Per-student detailed data
  const studentData = useMemo(() => {
    return allStudents.map(student => {
      const studentId = (student._id || student.id)?.toString();
      const name = `${student.profile?.firstName || ''} ${student.profile?.lastName || ''}`.trim();
      const batch = student.assignedCourses?.find(
        c => c.courseId?.toString() === assignedCourse?._id?.toString()
      )?.batch || '';

      const studentMonthEntries = monthEntries.filter(e => {
        const eid = (e.studentId?._id || e.studentId)?.toString();
        return eid === studentId;
      });

      const totalEntries = studentMonthEntries.length;
      const verifiedEntries = studentMonthEntries.filter(e => e.status === 'approved').length;
      const pendingEntries = studentMonthEntries.filter(
        e => e.status === 'submitted' || e.status === 'under_review'
      ).length;

      let lastSubmission = null;
      studentMonthEntries.forEach(e => {
        const d = new Date(e.metadata?.submittedAt || e.createdAt);
        if (!lastSubmission || d > lastSubmission) lastSubmission = d;
      });

      // Primary verifier
      const verifierCounts = {};
      studentMonthEntries.forEach(e => {
        if (e.review?.reviewerName) {
          verifierCounts[e.review.reviewerName] = (verifierCounts[e.review.reviewerName] || 0) + 1;
        }
      });
      const primaryVerifier = Object.entries(verifierCounts)
        .sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

      return {
        id: studentId,
        name,
        email: student.email || '',
        batch,
        totalEntries,
        verifiedEntries,
        pendingEntries,
        lastSubmission,
        primaryVerifier
      };
    });
  }, [allStudents, monthEntries, assignedCourse]);

  // Sorted and filtered student data
  const filteredStudentData = useMemo(() => {
    let data = [...studentData];

    if (studentSearch) {
      const search = studentSearch.toLowerCase();
      data = data.filter(s =>
        s.name.toLowerCase().includes(search) ||
        s.email.toLowerCase().includes(search) ||
        s.primaryVerifier.toLowerCase().includes(search)
      );
    }

    data.sort((a, b) => {
      let valA, valB;
      switch (studentSort.field) {
        case 'name': valA = a.name; valB = b.name; break;
        case 'total': valA = a.totalEntries; valB = b.totalEntries; break;
        case 'verified': valA = a.verifiedEntries; valB = b.verifiedEntries; break;
        case 'pending': valA = a.pendingEntries; valB = b.pendingEntries; break;
        case 'lastSubmission':
          valA = a.lastSubmission || new Date(0);
          valB = b.lastSubmission || new Date(0);
          break;
        default: valA = a.name; valB = b.name;
      }
      if (typeof valA === 'string') {
        return studentSort.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return studentSort.direction === 'asc' ? valA - valB : valB - valA;
    });

    return data;
  }, [studentData, studentSearch, studentSort]);

  // Month navigation
  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + direction);
    const newYear = newDate.getFullYear();
    const newMonth = String(newDate.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${newYear}-${newMonth}`);
  };

  const selectMonth = (monthIndex) => {
    const newMonth = String(monthIndex + 1).padStart(2, '0');
    setSelectedMonth(`${pickerYear}-${newMonth}`);
    setShowMonthPicker(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) {
        setShowMonthPicker(false);
      }
    };
    if (showMonthPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMonthPicker]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleStudentSort = (field) => {
    setStudentSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDownloadReport = async (reportType) => {
    if (generatingReport) return;
    setGeneratingReport(reportType);
    try {
      await generateMonitorReport({
        reportType,
        selectedMonth,
        allEntries,
        allStudents,
        courseTeachers,
        modules,
        assignedCourse,
        collegeName: collegeConfig.collegeName,
        logoSrc: collegeConfig.logo,
      });
    } catch (err) {
      console.error('Report generation failed:', err);
    } finally {
      setGeneratingReport(null);
    }
  };

  const sendGuideVerificationReminder = async () => {
    if (!assignedCourse || sendingGuideNotif) return;
    setSendingGuideNotif(true);
    setGuideNotifResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/push/notify-guides`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ courseId: assignedCourse._id })
      });
      const data = await response.json();
      setGuideNotifResult(data);
    } catch (error) {
      setGuideNotifResult({ success: false, message: 'Failed to send notification' });
    } finally {
      setSendingGuideNotif(false);
      setTimeout(() => setGuideNotifResult(null), 6000);
    }
  };

  const sendSingleGuideReminder = async (teacherId) => {
    if (!assignedCourse || notifyingGuide) return;
    setNotifyingGuide(teacherId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/push/notify-single-guide`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ teacherId, courseId: assignedCourse._id })
      });
      const data = await response.json();
      setGuideNotifResults(prev => ({ ...prev, [teacherId]: data.success }));
    } catch {
      setGuideNotifResults(prev => ({ ...prev, [teacherId]: false }));
    } finally {
      setNotifyingGuide(null);
      setTimeout(() => setGuideNotifResults(prev => {
        const next = { ...prev };
        delete next[teacherId];
        return next;
      }), 4000);
    }
  };

  const getComplianceColor = (percentage) => {
    if (percentage >= 80) return '#2ECC71';
    if (percentage >= 50) return '#F39C12';
    return '#E74C3C';
  };

  const getModuleColor = (name) => {
    const colors = ['#3498DB', '#E67E22', '#2ECC71', '#9B59B6', '#E74C3C', '#1ABC9C', '#F39C12', '#34495E'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) {
    return (
      <div className="md-container">
        <div className="md-loading">
          <div className="spinner"></div>
          <p>Loading department data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="md-container">
      {/* Header */}
      <div className="md-header">
        <div className="md-title-section">
          <h2><FaBuilding style={{ marginRight: '10px' }} /> Monitor Department</h2>
          <p className="md-subtitle">
            Department overview for {assignedCourse?.name} ({assignedCourse?.code})
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {guideNotifResult && (
            <span style={{
              fontSize: '13px',
              fontWeight: '500',
              color: guideNotifResult.success ? '#2ECC71' : '#E74C3C'
            }}>
              {guideNotifResult.message}
            </span>
          )}
          <button
            onClick={sendGuideVerificationReminder}
            disabled={sendingGuideNotif}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              background: sendingGuideNotif ? '#bbb' : '#9B59B6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: sendingGuideNotif ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={e => { if (!sendingGuideNotif) e.currentTarget.style.background = '#7d3c98'; }}
            onMouseLeave={e => { if (!sendingGuideNotif) e.currentTarget.style.background = '#9B59B6'; }}
          >
            <FaBell />
            {sendingGuideNotif ? 'Sending...' : 'Notify Guides to Verify'}
          </button>
        </div>
      </div>

      {error && <div className="md-error">{error}</div>}

      {/* Download Reports */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#555', flexShrink: 0 }}>
          <FaFilePdf style={{ color: '#E74C3C', fontSize: '18px' }} />
          <span style={{ fontWeight: '600', fontSize: '14px' }}>Download Report:</span>
        </div>
        {[
          { type: 'daily',   label: 'Daily',   color: '#3498DB', hover: '#2980B9' },
          { type: 'weekly',  label: 'Weekly',  color: '#27AE60', hover: '#1E8449' },
          { type: 'monthly', label: 'Monthly', color: '#8E44AD', hover: '#7D3C98' },
        ].map(({ type, label, color, hover }) => (
          <button
            key={type}
            onClick={() => handleDownloadReport(type)}
            disabled={!!generatingReport}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              padding: '9px 18px',
              background: generatingReport === type ? color : color,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: generatingReport ? 'not-allowed' : 'pointer',
              opacity: generatingReport && generatingReport !== type ? 0.55 : 1,
              transition: 'all 0.2s',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            }}
            onMouseEnter={e => { if (!generatingReport) e.currentTarget.style.background = hover; }}
            onMouseLeave={e => { if (!generatingReport) e.currentTarget.style.background = color; }}
          >
            <FaDownload style={{ fontSize: '11px' }} />
            {generatingReport === type ? `Generating ${label}…` : `${label} Report`}
          </button>
        ))}
        <span style={{ fontSize: '12px', color: '#999', marginLeft: 'auto' }}>
          Monthly report uses the selected month above
        </span>
      </div>

      {/* Month Navigator */}
      <div className="md-month-nav" ref={monthPickerRef}>
        <button className="md-month-btn" onClick={() => navigateMonth(-1)}>
          <FaChevronLeft />
        </button>
        <div
          className="md-month-label"
          onClick={() => {
            const [year] = selectedMonth.split('-').map(Number);
            setPickerYear(year);
            setShowMonthPicker(!showMonthPicker);
          }}
        >
          <FaCalendarAlt style={{ marginRight: '8px' }} />
          {getMonthName(selectedMonth)}
          <FaChevronDown style={{ marginLeft: '6px', fontSize: '12px' }} />
        </div>
        <button className="md-month-btn" onClick={() => navigateMonth(1)}>
          <FaChevronRight />
        </button>

        {showMonthPicker && (
          <div className="md-month-picker">
            <div className="md-picker-year-nav">
              <button onClick={() => setPickerYear(y => y - 1)}><FaChevronLeft /></button>
              <span>{pickerYear}</span>
              <button onClick={() => setPickerYear(y => y + 1)}><FaChevronRight /></button>
            </div>
            <div className="md-picker-months">
              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => {
                const isSelected = selectedMonth === `${pickerYear}-${String(i + 1).padStart(2, '0')}`;
                return (
                  <button
                    key={m}
                    className={`md-picker-month ${isSelected ? 'selected' : ''}`}
                    onClick={() => selectMonth(i)}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Overview Stats */}
      <div className="md-stats-grid">
        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: '#3498DB' }}>
            <FaUserGraduate />
          </div>
          <div className="md-stat-content">
            <h3>{overviewStats.totalStudents}</h3>
            <p>Total Students</p>
          </div>
        </div>
        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: '#9B59B6' }}>
            <FaChalkboardTeacher />
          </div>
          <div className="md-stat-content">
            <h3>{overviewStats.totalTeachers}</h3>
            <p>Faculty Members</p>
          </div>
        </div>
        <div className="md-stat-card">
          <div className="md-stat-icon" style={{ background: '#E67E22' }}>
            <FaBookMedical />
          </div>
          <div className="md-stat-content">
            <h3>{overviewStats.totalEntries}</h3>
            <p>Entries This Month</p>
          </div>
        </div>
        <div className="md-stat-card">
          <div className="md-stat-icon" style={{
            background: overviewStats.verificationRate >= 70 ? '#2ECC71'
              : overviewStats.verificationRate >= 40 ? '#F39C12' : '#E74C3C'
          }}>
            <FaCheckCircle />
          </div>
          <div className="md-stat-content">
            <h3>{overviewStats.verificationRate}%</h3>
            <p>Verification Rate</p>
          </div>
        </div>
      </div>

      {/* Section 1: Teacher Verification Activity */}
      <div className="md-section">
        <div className="md-section-header" onClick={() => toggleSection('teachers')}>
          <h3>
            <FaChalkboardTeacher style={{ marginRight: '10px', color: '#9B59B6' }} />
            Faculty Verification Activity
          </h3>
          <span className="md-expand-icon">
            {expandedSections.teachers ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
        {expandedSections.teachers && (
          <div className="md-section-content">
            {teacherData.length === 0 ? (
              <div className="md-empty-state">
                <p>No faculty verification data available.</p>
              </div>
            ) : (
              <div className="md-teacher-list">
                {teacherData.map((teacher, index) => {
                  const isTeacherExpanded = expandedTeachers[teacher.id];
                  const details = teacherVerificationDetails[teacher.id] || [];
                  return (
                    <div key={teacher.id || index} className={`md-teacher-card ${isTeacherExpanded ? 'expanded' : ''}`}>
                      <div className="md-teacher-clickable" onClick={() => toggleTeacherExpand(teacher.id)}>
                        <div className="md-teacher-info">
                          <div className="md-teacher-avatar">
                            {teacher.name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)}
                          </div>
                          <div className="md-teacher-details">
                            <h4>{teacher.name}</h4>
                            <div className="md-teacher-meta">
                              <span className="md-teacher-stat">
                                <FaCheckCircle /> {teacher.monthVerifications} this month
                              </span>
                              <span className="md-teacher-stat">
                                <FaClipboardList /> {teacher.totalVerifications} total
                              </span>
                              <span className="md-teacher-stat">
                                <FaClock /> {teacher.lastVerificationDate ? formatDate(teacher.lastVerificationDate) : 'Never'}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); sendSingleGuideReminder(teacher.id); }}
                            disabled={!!notifyingGuide}
                            title="Send verification reminder to this guide"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '5px 11px',
                              fontSize: '12px',
                              fontWeight: '600',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: notifyingGuide ? 'not-allowed' : 'pointer',
                              background: guideNotifResults[teacher.id] === true
                                ? '#2ECC71'
                                : guideNotifResults[teacher.id] === false
                                ? '#E74C3C'
                                : '#9B59B6',
                              color: 'white',
                              flexShrink: 0,
                              opacity: notifyingGuide && notifyingGuide !== teacher.id ? 0.5 : 1,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <FaBell style={{ fontSize: '11px' }} />
                            {notifyingGuide === teacher.id
                              ? 'Sending...'
                              : guideNotifResults[teacher.id] === true
                              ? 'Sent!'
                              : guideNotifResults[teacher.id] === false
                              ? 'Failed'
                              : 'Notify'}
                          </button>
                          <span className="md-teacher-expand-icon">
                            {isTeacherExpanded ? <FaChevronUp /> : <FaChevronDown />}
                          </span>
                        </div>
                        <div className="md-teacher-bar-container">
                          <div
                            className="md-teacher-bar"
                            style={{
                              width: `${(teacher.monthVerifications / maxTeacherVerifications) * 100}%`,
                              background: teacher.monthVerifications > 0 ? '#9B59B6' : '#e0e0e0'
                            }}
                          />
                        </div>
                        {teacher.monthVerifications === 0 && (
                          <div className="md-teacher-warning">
                            <FaExclamationTriangle /> No verifications this month
                          </div>
                        )}
                      </div>

                      {isTeacherExpanded && (
                        <div className="md-teacher-expanded">
                          {details.length === 0 ? (
                            <p className="md-teacher-no-details">No verification records for this month.</p>
                          ) : (
                            <>
                              {(() => {
                                // Group by module
                                const moduleGroups = {};
                                details.forEach(d => {
                                  if (!moduleGroups[d.moduleName]) {
                                    moduleGroups[d.moduleName] = { students: new Set(), count: 0 };
                                  }
                                  moduleGroups[d.moduleName].students.add(d.studentName);
                                  moduleGroups[d.moduleName].count++;
                                });
                                const sortedModules = Object.entries(moduleGroups)
                                  .sort((a, b) => b[1].count - a[1].count);
                                const uniqueStudents = new Set(details.map(d => d.studentName));

                                return (
                                  <>
                                    <div className="md-teacher-modules">
                                      {sortedModules.map(([moduleName, data]) => (
                                        <div key={moduleName} className="md-teacher-module-card">
                                          <div className="md-module-card-header">
                                            <span className="md-module-card-dot" style={{ background: getModuleColor(moduleName) }} />
                                            <span className="md-module-card-name">{moduleName}</span>
                                            <span className="md-module-card-count">{data.count} {data.count === 1 ? 'entry' : 'entries'}</span>
                                          </div>
                                          <div className="md-module-card-students">
                                            {[...data.students].map((name, i) => (
                                              <span key={i} className="md-module-student-chip">
                                                {name}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="md-teacher-detail-summary">
                                      <span><FaUserGraduate /> {uniqueStudents.size} student{uniqueStudents.size !== 1 ? 's' : ''}</span>
                                      <span><FaLayerGroup /> {sortedModules.length} module{sortedModules.length !== 1 ? 's' : ''}</span>
                                      <span><FaCheckCircle /> {details.length} verification{details.length !== 1 ? 's' : ''}</span>
                                    </div>
                                  </>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 2: Weekly Compliance */}
      <div className="md-section">
        <div className="md-section-header" onClick={() => toggleSection('compliance')}>
          <h3>
            <FaCalendarAlt style={{ marginRight: '10px', color: '#2ECC71' }} />
            Student Weekly Submission Compliance
            <span className="md-compliance-badge" style={{ background: getComplianceColor(overallCompliance) }}>
              {overallCompliance}%
            </span>
          </h3>
          <span className="md-expand-icon">
            {expandedSections.compliance ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
        {expandedSections.compliance && (
          <div className="md-section-content">
            {weeklyCompliance.length === 0 ? (
              <div className="md-empty-state">
                <p>No weekly data available.</p>
              </div>
            ) : (
              <div className="md-weeks-list">
                {weeklyCompliance.map((week, index) => (
                  <div key={index} className="md-week-card">
                    <div className="md-week-header">
                      <span className="md-week-label">Week {index + 1}</span>
                      <span className="md-week-dates">{week.label}</span>
                      <span
                        className="md-week-compliance"
                        style={{ color: getComplianceColor(week.compliance) }}
                      >
                        {week.compliance}% ({week.submitted}/{week.total})
                      </span>
                    </div>
                    <div className="md-week-bar-bg">
                      <div
                        className="md-week-bar-fill"
                        style={{
                          width: `${week.compliance}%`,
                          background: getComplianceColor(week.compliance)
                        }}
                      />
                    </div>
                    {week.nonCompliant.length > 0 && week.nonCompliant.length <= 10 && (
                      <div className="md-week-non-compliant">
                        <span className="md-nc-label">
                          <FaExclamationTriangle /> Not submitted ({week.nonCompliant.length}):
                        </span>
                        <div className="md-nc-students">
                          {week.nonCompliant.map((student, i) => (
                            <span key={i} className="md-nc-chip">
                              {student.profile?.firstName} {student.profile?.lastName}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {week.nonCompliant.length > 10 && (
                      <div className="md-week-non-compliant">
                        <span className="md-nc-label">
                          <FaExclamationTriangle /> {week.nonCompliant.length} students have not submitted
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 3: Activity Distribution */}
      <div className="md-section">
        <div className="md-section-header" onClick={() => toggleSection('activities')}>
          <h3>
            <FaLayerGroup style={{ marginRight: '10px', color: '#E67E22' }} />
            Activity Distribution
          </h3>
          <span className="md-expand-icon">
            {expandedSections.activities ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
        {expandedSections.activities && (
          <div className="md-section-content">
            {activityDistribution.length === 0 ? (
              <div className="md-empty-state">
                <p>No activities recorded this month.</p>
              </div>
            ) : (
              <div className="md-activity-list">
                {activityDistribution.map((activity, index) => {
                  const color = getModuleColor(activity.name);
                  return (
                    <div key={index} className="md-activity-row">
                      <div className="md-activity-info">
                        <span className="md-activity-dot" style={{ background: color }} />
                        <span className="md-activity-name">{activity.name}</span>
                      </div>
                      <div className="md-activity-bar-section">
                        <div className="md-activity-bar-bg">
                          <div
                            className="md-activity-bar-fill"
                            style={{
                              width: `${(activity.count / maxActivityCount) * 100}%`,
                              background: color
                            }}
                          />
                        </div>
                        <div className="md-activity-stats">
                          <span className="md-activity-count">{activity.count} entries</span>
                          <span className="md-activity-students">{activity.studentCount} students</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 4: Student-wise Status */}
      <div className="md-section">
        <div className="md-section-header" onClick={() => toggleSection('students')}>
          <h3>
            <FaUserGraduate style={{ marginRight: '10px', color: '#3498DB' }} />
            Student-wise Logbook Status
          </h3>
          <span className="md-expand-icon">
            {expandedSections.students ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
        {expandedSections.students && (
          <div className="md-section-content">
            {/* Search */}
            <div className="md-student-search">
              <FaSearch className="md-search-icon" />
              <input
                type="text"
                placeholder="Search by student name, email, or verifier..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="md-search-input"
              />
              {studentSearch && (
                <button className="md-search-clear" onClick={() => setStudentSearch('')}>
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Table */}
            <div className="md-table-wrapper">
              <table className="md-student-table">
                <thead>
                  <tr>
                    <th onClick={() => handleStudentSort('name')}>
                      Student {studentSort.field === 'name' ? (studentSort.direction === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th onClick={() => handleStudentSort('total')}>
                      Entries {studentSort.field === 'total' ? (studentSort.direction === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th onClick={() => handleStudentSort('verified')} className="md-hide-mobile">
                      Verified {studentSort.field === 'verified' ? (studentSort.direction === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th onClick={() => handleStudentSort('pending')} className="md-hide-mobile">
                      Pending {studentSort.field === 'pending' ? (studentSort.direction === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th onClick={() => handleStudentSort('lastSubmission')} className="md-hide-mobile">
                      Last Submitted {studentSort.field === 'lastSubmission' ? (studentSort.direction === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="md-hide-mobile">Verified By</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudentData.map(student => (
                    <tr key={student.id}>
                      <td>
                        <div className="md-student-cell">
                          <div className="md-student-cell-avatar">
                            {student.name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)}
                          </div>
                          <div className="md-student-cell-info">
                            <span className="md-student-cell-name">{student.name}</span>
                            <span className="md-student-cell-email">{student.email}</span>
                            {student.batch && (
                              <span className="md-student-cell-batch">
                                {batchLabels[student.batch] || student.batch}
                              </span>
                            )}
                            {/* Mobile-only details */}
                            <div className="md-mobile-details">
                              <span className="md-mobile-badge verified">{student.verifiedEntries} verified</span>
                              <span className="md-mobile-badge pending">{student.pendingEntries} pending</span>
                              {student.primaryVerifier !== '-' && (
                                <span className="md-mobile-verifier">By: {student.primaryVerifier}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`md-entry-count ${student.totalEntries === 0 ? 'zero' : ''}`}>
                          {student.totalEntries}
                        </span>
                      </td>
                      <td className="md-hide-mobile">
                        <span className="md-badge-verified">{student.verifiedEntries}</span>
                      </td>
                      <td className="md-hide-mobile">
                        <span className={`md-badge-pending ${student.pendingEntries > 0 ? 'has-pending' : ''}`}>
                          {student.pendingEntries}
                        </span>
                      </td>
                      <td className="md-hide-mobile">
                        {student.lastSubmission ? formatDate(student.lastSubmission) : '-'}
                      </td>
                      <td className="md-hide-mobile">
                        {student.primaryVerifier}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredStudentData.length === 0 && (
              <div className="md-empty-state">
                <p>{studentSearch ? `No students match "${studentSearch}"` : 'No student data available.'}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section: Exam Eligibility */}
      <div className="md-section">
        <div className="md-section-header" onClick={() => toggleSection('eligibility')}>
          <h3>
            <FaClipboardCheck style={{ marginRight: '10px', color: '#16a085' }} />
            Exam Eligibility Check
          </h3>
          <span className="md-expand-icon">
            {expandedSections.eligibility ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
        {expandedSections.eligibility && (
          <div className="md-section-content">
            {/* Batch filter + fetch button */}
            <div className="md-eligibility-toolbar">
              <div className="md-eligibility-filter">
                <FaFilter style={{ color: '#718096', fontSize: '13px' }} />
                <select
                  value={eligibilityBatch}
                  onChange={e => { setEligibilityBatch(e.target.value); setEligibilityFetched(false); }}
                  className="md-eligibility-select"
                >
                  <option value="">All Batches</option>
                  {availableBatches.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              <button
                className="md-eligibility-btn"
                onClick={fetchEligibilityBatch}
                disabled={eligibilityLoading}
              >
                {eligibilityLoading
                  ? <><FaSyncAlt style={{ animation: 'spin 1s linear infinite' }} /> Checking…</>
                  : <><FaClipboardCheck /> Check Eligibility</>
                }
              </button>
            </div>

            {eligibilityError && (
              <div className="md-eligibility-error">
                <FaExclamationTriangle /> {eligibilityError}
              </div>
            )}

            {eligibilityFetched && !eligibilityLoading && (
              <EligibilityCards eligibilityList={eligibilityList} onViewDocuments={setDocsModalStudent} />
            )}

            {!eligibilityFetched && !eligibilityLoading && (
              <div className="md-empty-state">
                <p>Select a batch (optional) and click <strong>Check Eligibility</strong> to view exam eligibility status for all students.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {docsModalStudent && (
        <StudentDocumentsModal
          studentId={docsModalStudent.id}
          courseId={assignedCourse?._id}
          onClose={() => setDocsModalStudent(null)}
        />
      )}
    </div>
  );
};

export default MonitorDepartment;
